import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { ParkingLot } from '../lib/types';
import {
    convertGeoJSONToGoogleMaps,
    extractLineGeometryPaths,
    getPolygonColor,
    GoogleMapsLatLng
} from '../lib/utils';

export interface ParkingLotMapOverlayMarker {
    id: string;
    position: GoogleMapsLatLng;
    title?: string;
    label?: string;
    kind?: 'count' | 'search' | 'address';
    color?: string;
    textColor?: string;
    scale?: number;
    zIndex?: number;
    opacity?: number;
    onClick?: () => void;
}

export interface ParkingLotMapViewportTarget {
    key: string;
    position: GoogleMapsLatLng;
    zoom: number;
}

export interface ParkingLotMapViewport {
    bbox: string;
    zoom: number;
}

interface ParkingLotMapProps {
    parkingLots: ParkingLot[];
    selectedPlace: ParkingLot | null;
    editMode: boolean;
    creatingNew: boolean;
    currentCoordinates: GoogleMapsLatLng[];
    /**
     * When this value changes, we re-run initial viewport fitBounds once.
     * Use this to avoid "random" viewport jumps on unrelated rerenders.
     */
    fitBoundsKey?: string;
    /** Session-only: lotIds to hide on map (so overlapping polygons can be tapped). */
    hiddenInMapView?: Set<string>;
    polygonStylesByLotId?: Record<string, Partial<google.maps.PolygonOptions>>;
    overlayMarkers?: ParkingLotMapOverlayMarker[];
    viewportTarget?: ParkingLotMapViewportTarget | null;
    onViewportChange?: (viewport: ParkingLotMapViewport) => void;
    autoFitBounds?: boolean;
    /** When true, clicking polygons is suppressed and dragging draws a selection rectangle. */
    bulkSelectMode?: boolean;
    onMapClick?: (event: google.maps.MapMouseEvent) => void;
    onPlaceSelect: (place: ParkingLot) => void;
    onPolygonEdit?: (place: ParkingLot, newPaths: GoogleMapsLatLng[]) => void;
    onMarkerDragEnd?: (index: number, newPosition: GoogleMapsLatLng) => void;
    onMarkerClick?: (index: number) => void;
    /** Called when user finishes drawing a selection rectangle in bulk select mode. */
    onBulkRectDrawn?: (north: number, south: number, east: number, west: number) => void;
}

interface LotPointMarker {
    lot: ParkingLot;
    position: GoogleMapsLatLng;
}

interface LotLineFeature {
    lot: ParkingLot;
    paths: GoogleMapsLatLng[][];
}

const mapContainerStyle = {
    width: '100%',
    height: '100%'
};

const defaultCenter = {
    lat: 57.048,
    lng: 9.919
};

const defaultZoom = 13;

const ParkingLotMap: React.FC<ParkingLotMapProps> = ({
    parkingLots,
    selectedPlace,
    editMode,
    creatingNew,
    currentCoordinates,
    fitBoundsKey,
    hiddenInMapView,
    polygonStylesByLotId,
    overlayMarkers = [],
    viewportTarget,
    onViewportChange,
    autoFitBounds = true,
    bulkSelectMode = false,
    onMapClick,
    onPlaceSelect,
    onPolygonEdit,
    onMarkerDragEnd,
    onMarkerClick,
    onBulkRectDrawn,
}) => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    const { isLoaded: isGoogleMapsScriptLoaded, loadError } = useJsApiLoader({
        id: 'google-maps-script',
        googleMapsApiKey: apiKey,
    });
    const mapRef = useRef<google.maps.Map | null>(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [isRenderingPolygons, setIsRenderingPolygons] = useState(false);
    const [renderedCount, setRenderedCount] = useState(0);
    const polygonsRef = useRef<Map<string, google.maps.Polygon>>(new Map());
    const polygonListenersRef = useRef<Map<string, google.maps.MapsEventListener[]>>(new Map());
    const polylinesRef = useRef<Map<string, google.maps.Polyline[]>>(new Map());
    const polylineListenersRef = useRef<Map<string, google.maps.MapsEventListener[]>>(new Map());
    const hasFittedBoundsRef = useRef(false);
    const lastFitBoundsKeyRef = useRef<string | undefined>(undefined);
    const onPlaceSelectRef = useRef(onPlaceSelect);
    const onPolygonEditRef = useRef(onPolygonEdit);
    const getPolygonOptionsRef = useRef<((lot: ParkingLot, isSelected: boolean, isEditable: boolean) => google.maps.PolygonOptions) | null>(null);
    const interactionStateRef = useRef({
        editMode,
        creatingNew,
        selectedPlaceId: selectedPlace?._id ?? null
    });
    const isSyncingPathsFromReactRef = useRef(false);

    const draftPolygonRef = useRef<google.maps.Polygon | null>(null);
    const draftPolygonListenersRef = useRef<google.maps.MapsEventListener[]>([]);

    const bulkRectRef = useRef<google.maps.Rectangle | null>(null);
    const bulkListenersRef = useRef<google.maps.MapsEventListener[]>([]);
    const onBulkRectDrawnRef = useRef(onBulkRectDrawn);
    const onViewportChangeRef = useRef(onViewportChange);
    const lastViewportTargetKeyRef = useRef<string | null>(null);

    const getPolygonOptions = useCallback((
        lot: ParkingLot,
        isSelected: boolean,
        isEditable: boolean
    ): google.maps.PolygonOptions => {
        const customStyle = polygonStylesByLotId?.[lot.lotId] ?? {};
        const defaultColor = getPolygonColor(lot.parking_type);
        const baseFillColor = customStyle.fillColor ?? defaultColor;
        const baseFillOpacity = customStyle.fillOpacity ?? 0.6;
        const baseStrokeColor = customStyle.strokeColor ?? baseFillColor;
        const baseStrokeWeight = customStyle.strokeWeight ?? 4;
        const selectedFillOpacity = Math.min(1, Math.max(baseFillOpacity, 0.35) + 0.18);

        return {
            fillColor: baseFillColor,
            fillOpacity: isSelected ? selectedFillOpacity : baseFillOpacity,
            strokeColor: isSelected ? '#111827' : baseStrokeColor,
            strokeOpacity: customStyle.strokeOpacity ?? 1,
            strokeWeight: isSelected ? Math.max(baseStrokeWeight, 2) + 1 : baseStrokeWeight,
            editable: isEditable,
            draggable: false,
            clickable: customStyle.clickable ?? true,
            zIndex: isSelected ? Math.max((customStyle.zIndex as number | undefined) ?? 1, 1000) : (customStyle.zIndex as number | undefined) ?? 1
        };
    }, [polygonStylesByLotId]);

    const getLineOptions = useCallback((lot: ParkingLot, isSelected: boolean): google.maps.PolylineOptions => {
        const customStyle = polygonStylesByLotId?.[lot.lotId] ?? {};
        const baseColor = customStyle.strokeColor ?? customStyle.fillColor ?? getPolygonColor(lot.parking_type);
        const baseWeight = customStyle.strokeWeight ?? 5;
        return {
            strokeColor: isSelected ? '#111827' : baseColor,
            strokeOpacity: customStyle.strokeOpacity ?? 0.9,
            strokeWeight: isSelected ? Math.max(baseWeight, 5) + 2 : baseWeight,
            clickable: customStyle.clickable ?? true,
            zIndex: isSelected ? 1000 : ((customStyle.zIndex as number | undefined) ?? 1),
        };
    }, [polygonStylesByLotId]);

    useEffect(() => {
        onPlaceSelectRef.current = onPlaceSelect;
    }, [onPlaceSelect]);

    useEffect(() => {
        onPolygonEditRef.current = onPolygonEdit;
    }, [onPolygonEdit]);

    useEffect(() => {
        onBulkRectDrawnRef.current = onBulkRectDrawn;
    }, [onBulkRectDrawn]);

    useEffect(() => {
        onViewportChangeRef.current = onViewportChange;
    }, [onViewportChange]);

    useEffect(() => {
        getPolygonOptionsRef.current = getPolygonOptions;
    }, [getPolygonOptions]);

    useEffect(() => {
        interactionStateRef.current = {
            editMode,
            creatingNew,
            selectedPlaceId: selectedPlace?._id ?? null
        };
    }, [editMode, creatingNew, selectedPlace]);

    // Split polygon lots from point lots and prepare bounds calculation.
    const { validPolygons, lineFeatures, pointMarkers } = useMemo(() => {
        const valid: Array<{ lot: ParkingLot; paths: GoogleMapsLatLng[] }> = [];
        const lines: LotLineFeature[] = [];
        const points: LotPointMarker[] = [];

        parkingLots.forEach(lot => {
            if (lot.geometry) {
                if (lot.geometry.type === 'LineString' || lot.geometry.type === 'MultiLineString') {
                    const paths = extractLineGeometryPaths(lot.geometry);
                    if (paths.length > 0) lines.push({ lot, paths });
                    return;
                }
                const paths = convertGeoJSONToGoogleMaps(lot.geometry);
                if (paths.length > 0) {
                    if (lot.geometry.type === 'Point') {
                        points.push({ lot, position: paths[0] });
                    } else {
                        valid.push({ lot, paths });
                    }
                }
            }
        });

        return { validPolygons: valid, lineFeatures: lines, pointMarkers: points };
    }, [parkingLots]);

    const visiblePolygons = useMemo(
        () => validPolygons.filter(p => !hiddenInMapView?.has(p.lot.lotId)),
        [validPolygons, hiddenInMapView]
    );
    const visiblePolygonMap = useMemo(
        () => new Map(visiblePolygons.map(({ lot, paths }) => [lot.lotId, { lot, paths }])),
        [visiblePolygons]
    );
    const visibleLineFeatures = useMemo(
        () => lineFeatures.filter(({ lot }) => !hiddenInMapView?.has(lot.lotId)),
        [lineFeatures, hiddenInMapView]
    );
    const visibleLineMap = useMemo(
        () => new Map(visibleLineFeatures.map(({ lot, paths }) => [lot.lotId, { lot, paths }])),
        [visibleLineFeatures]
    );
    const googleMapsReady = Boolean(isMapLoaded && window.google?.maps);

    const renderEditMarkers = useMemo(() => {
        // For existing polygons, Google Maps' native editable handles are smoother than rendering
        // a Marker per vertex (which also causes more React work). We keep subtle vertex handles
        // only while creating a new polygon.
        if (!editMode || !creatingNew || currentCoordinates.length === 0 || !googleMapsReady) return null;

        return currentCoordinates.map((coord, index) => (
            <Marker
                key={`vertex-${index}`}
                position={coord}
                draggable={true}
                icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 6,
                    fillColor: '#7c3aed',
                    fillOpacity: 0.95,
                    strokeColor: '#ffffff',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                }}
                zIndex={2000}
                onDragEnd={(e: google.maps.MapMouseEvent) => {
                    if (onMarkerDragEnd && e.latLng) {
                        onMarkerDragEnd(index, {
                            lat: e.latLng.lat(),
                            lng: e.latLng.lng()
                        });
                    }
                }}
                onClick={() => {
                    if (onMarkerClick) {
                        onMarkerClick(index);
                    }
                }}
            />
        ));
    }, [editMode, creatingNew, currentCoordinates, onMarkerDragEnd, onMarkerClick, googleMapsReady]);

    const renderPointMarkers = useMemo(() => {
        if (!googleMapsReady) return null;

        return pointMarkers
            .filter(({ lot }) => !hiddenInMapView?.has(lot.lotId))
            .map(({ lot, position }) => {
                const isSelected = selectedPlace?._id === lot._id;
                const color = getPolygonColor(lot.parking_type);

                return (
                    <Marker
                        key={`lot-point-${lot.lotId}`}
                        position={position}
                        title={lot.name || lot.lotId}
                        zIndex={isSelected ? 1200 : 500}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: isSelected ? 9 : 7,
                            fillColor: color,
                            fillOpacity: 0.95,
                            strokeColor: isSelected ? '#111827' : '#ffffff',
                            strokeOpacity: 1,
                            strokeWeight: isSelected ? 3 : 2,
                        }}
                        onClick={() => {
                            const { editMode: isEditing, creatingNew: isCreatingNew } = interactionStateRef.current;
                            if (!isEditing || isCreatingNew) {
                                onPlaceSelectRef.current(lot);
                            }
                        }}
                    />
                );
            });
    }, [googleMapsReady, hiddenInMapView, onPlaceSelectRef, pointMarkers, selectedPlace]);

    // Keep a stable center prop to avoid unnecessary map viewport resets.
    // We use fitBounds once (see effect below) for initial viewport framing.
    const stableCenter = useMemo(() => defaultCenter, []);

    const emitViewport = useCallback(() => {
        const map = mapRef.current;
        const bounds = map?.getBounds();
        const zoom = map?.getZoom();
        if (!bounds || zoom == null) return;
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();
        const rounded = (value: number) => value.toFixed(6);
        onViewportChangeRef.current?.({
            bbox: [
                rounded(southWest.lng()),
                rounded(southWest.lat()),
                rounded(northEast.lng()),
                rounded(northEast.lat()),
            ].join(','),
            zoom,
        });
    }, []);

    useEffect(() => {
        if (!isMapLoaded || !mapRef.current || !viewportTarget) return;
        if (lastViewportTargetKeyRef.current === viewportTarget.key) return;
        lastViewportTargetKeyRef.current = viewportTarget.key;
        mapRef.current.panTo(viewportTarget.position);
        mapRef.current.setZoom(viewportTarget.zoom);
    }, [isMapLoaded, viewportTarget]);

    const clearPolygonListeners = useCallback((lotId: string) => {
        const listeners = polygonListenersRef.current.get(lotId) ?? [];
        listeners.forEach((listener) => listener.remove());
        polygonListenersRef.current.delete(lotId);
    }, []);

    const clearPolylineListeners = useCallback((lotId: string) => {
        const listeners = polylineListenersRef.current.get(lotId) ?? [];
        listeners.forEach((listener) => listener.remove());
        polylineListenersRef.current.delete(lotId);
    }, []);

    const syncPolygonPathToParent = useCallback((lot: ParkingLot, polygon: google.maps.Polygon) => {
        if (isSyncingPathsFromReactRef.current) return;

        const { editMode: isEditing, creatingNew: isCreatingNew, selectedPlaceId } = interactionStateRef.current;
        const handlePolygonEdit = onPolygonEditRef.current;

        if (!isEditing || isCreatingNew || !handlePolygonEdit || selectedPlaceId !== lot._id) {
            return;
        }

        const path = polygon.getPath();
        const newPaths: GoogleMapsLatLng[] = [];
        path.forEach((latLng: google.maps.LatLng) => {
            newPaths.push({
                lat: latLng.lat(),
                lng: latLng.lng()
            });
        });

        handlePolygonEdit(lot, newPaths);
    }, []);

    const attachPolygonListeners = useCallback((lot: ParkingLot, polygon: google.maps.Polygon) => {
        clearPolygonListeners(lot.lotId);

        const listeners = [
            polygon.addListener('click', () => {
                const { editMode: isEditing, creatingNew: isCreatingNew } = interactionStateRef.current;
                if (!isEditing || isCreatingNew) {
                    onPlaceSelectRef.current(lot);
                }
            })
        ];

        const path = polygon.getPath();
        listeners.push(path.addListener('set_at', () => syncPolygonPathToParent(lot, polygon)));
        listeners.push(path.addListener('insert_at', () => syncPolygonPathToParent(lot, polygon)));
        listeners.push(path.addListener('remove_at', () => syncPolygonPathToParent(lot, polygon)));

        polygonListenersRef.current.set(lot.lotId, listeners);
    }, [clearPolygonListeners, syncPolygonPathToParent]);

    useEffect(() => {
        if (!isMapLoaded || !mapRef.current || !window.google?.maps) {
            return;
        }

        const map = mapRef.current;
        const visibleLotIds = new Set(visiblePolygons.map(({ lot }) => lot.lotId));
        const visibleLineLotIds = new Set(visibleLineFeatures.map(({ lot }) => lot.lotId));
        const totalRenderable = visiblePolygons.length + visibleLineFeatures.length;

        setRenderedCount(0);
        setIsRenderingPolygons(totalRenderable > 0);

        polygonsRef.current.forEach((polygon, lotId) => {
            if (!visibleLotIds.has(lotId)) {
                clearPolygonListeners(lotId);
                polygon.setMap(null);
                polygonsRef.current.delete(lotId);
            }
        });

        polylinesRef.current.forEach((polylines, lotId) => {
            if (!visibleLineLotIds.has(lotId)) {
                clearPolylineListeners(lotId);
                polylines.forEach((polyline) => polyline.setMap(null));
                polylinesRef.current.delete(lotId);
            }
        });

        visiblePolygons.forEach(({ lot, paths }, index) => {
            try {
                const { editMode: isEditing, creatingNew: isCreatingNew, selectedPlaceId } = interactionStateRef.current;
                const isSelected = selectedPlaceId === lot._id;
                const isEditable = isEditing && isSelected && !isCreatingNew;

                // While editing, drive the selected polygon's displayed geometry from `currentCoordinates`
                // so React state updates don't "snap" the polygon back to the original lot geometry.
                const sourcePaths =
                    isEditable && currentCoordinates.length >= 3
                        ? currentCoordinates
                        : paths;
                const polygonPaths = sourcePaths.map((path) => new window.google.maps.LatLng(path.lat, path.lng));

                let polygon = polygonsRef.current.get(lot.lotId);

                if (!polygon) {
                    polygon = new window.google.maps.Polygon({
                        paths: polygonPaths,
                        ...(getPolygonOptionsRef.current?.(lot, isSelected, isEditable) ?? {}),
                        map
                    });
                    polygonsRef.current.set(lot.lotId, polygon);
                } else {
                    isSyncingPathsFromReactRef.current = true;
                    polygon.setPaths(polygonPaths);
                    isSyncingPathsFromReactRef.current = false;
                    polygon.setMap(map);
                    polygon.setOptions(getPolygonOptionsRef.current?.(lot, isSelected, isEditable) ?? {});
                }

                attachPolygonListeners(lot, polygon);
            } catch (error) {
                // Keep the rest of the map interactive even if one polygon is malformed.
            } finally {
                setRenderedCount(index + 1);
            }
        });

        visibleLineFeatures.forEach(({ lot, paths }, index) => {
            try {
                clearPolylineListeners(lot.lotId);
                const previous = polylinesRef.current.get(lot.lotId) ?? [];
                const isSelected = interactionStateRef.current.selectedPlaceId === lot._id;
                const nextPolylines = paths.map((path, pathIndex) => {
                    const googlePath = path.map((point) => new window.google.maps.LatLng(point.lat, point.lng));
                    const polyline = previous[pathIndex] ?? new window.google.maps.Polyline();
                    polyline.setOptions({
                        ...getLineOptions(lot, isSelected),
                        path: googlePath,
                        map,
                    });
                    return polyline;
                });
                previous.slice(paths.length).forEach((polyline) => polyline.setMap(null));
                polylinesRef.current.set(lot.lotId, nextPolylines);
                polylineListenersRef.current.set(
                    lot.lotId,
                    nextPolylines.map((polyline) => polyline.addListener('click', () => {
                        const { editMode: isEditing, creatingNew: isCreatingNew } = interactionStateRef.current;
                        if (!isEditing || isCreatingNew) onPlaceSelectRef.current(lot);
                    }))
                );
            } catch (error) {
                // Keep rendering the remaining line features if one path is malformed.
            } finally {
                setRenderedCount(visiblePolygons.length + index + 1);
            }
        });

        setIsRenderingPolygons(false);

        const shouldResetBounds = fitBoundsKey !== lastFitBoundsKeyRef.current;
        if (shouldResetBounds) {
            hasFittedBoundsRef.current = false;
            lastFitBoundsKeyRef.current = fitBoundsKey;
        }

        if (autoFitBounds && !hasFittedBoundsRef.current && totalRenderable > 0) {
            requestAnimationFrame(() => {
                if (!mapRef.current || !window.google?.maps) {
                    return;
                }

                try {
                    const bounds = new window.google.maps.LatLngBounds();
                    visiblePolygons.forEach(({ paths }) => {
                        paths.forEach((path) => {
                            bounds.extend(new window.google.maps.LatLng(path.lat, path.lng));
                        });
                    });
                    visibleLineFeatures.forEach(({ paths }) => {
                        paths.flat().forEach((path) => {
                            bounds.extend(new window.google.maps.LatLng(path.lat, path.lng));
                        });
                    });

                    mapRef.current.fitBounds(bounds);
                    hasFittedBoundsRef.current = true;
                } catch (error) {
                    // Bounds fitting should never block map interaction.
                }
            });
        }
    }, [
        visiblePolygons,
        visibleLineFeatures,
        isMapLoaded,
        attachPolygonListeners,
        clearPolygonListeners,
        clearPolylineListeners,
        currentCoordinates,
        fitBoundsKey,
        getLineOptions,
        autoFitBounds,
    ]);

    // NOTE: We intentionally do NOT reset hasFittedBoundsRef on every visiblePolygons identity change.
    // That was causing unexpected viewport jumps during edit flows and unrelated rerenders.

    useEffect(() => {
        if (!isMapLoaded) {
            return;
        }

        polygonsRef.current.forEach((polygon, lotId) => {
            const polygonEntry = visiblePolygonMap.get(lotId);
            if (!polygonEntry) {
                return;
            }

            const { lot } = polygonEntry;
            const isSelected = selectedPlace?._id === lot._id;
            const isEditable = editMode && isSelected && !creatingNew;

            polygon.setOptions(getPolygonOptions(lot, isSelected, isEditable));
        });

        polylinesRef.current.forEach((polylines, lotId) => {
            const lineEntry = visibleLineMap.get(lotId);
            if (!lineEntry) return;
            const isSelected = selectedPlace?._id === lineEntry.lot._id;
            polylines.forEach((polyline) => polyline.setOptions(getLineOptions(lineEntry.lot, isSelected)));
        });
    }, [
        selectedPlace,
        visiblePolygonMap,
        visibleLineMap,
        isMapLoaded,
        editMode,
        creatingNew,
        getPolygonOptions,
        getLineOptions,
    ]);

    // Draft polygon for "creating new" flow: render it imperatively so it stays smooth.
    useEffect(() => {
        if (!isMapLoaded || !mapRef.current || !window.google?.maps) return;

        const map = mapRef.current;
        const shouldShowDraft = Boolean(editMode && creatingNew && currentCoordinates.length >= 2);

        // Clean up if we shouldn't show it.
        if (!shouldShowDraft) {
            draftPolygonListenersRef.current.forEach((l) => l.remove());
            draftPolygonListenersRef.current = [];
            if (draftPolygonRef.current) {
                draftPolygonRef.current.setMap(null);
                draftPolygonRef.current = null;
            }
            return;
        }

        const paths = currentCoordinates.map((p) => new window.google.maps.LatLng(p.lat, p.lng));

        if (!draftPolygonRef.current) {
            const poly = new window.google.maps.Polygon({
                paths,
                map,
                clickable: false,
                draggable: false,
                editable: true,
                strokeColor: '#7c3aed',
                strokeOpacity: 1,
                strokeWeight: 3,
                fillColor: '#a78bfa',
                fillOpacity: 0.22,
                zIndex: 2000,
            });
            draftPolygonRef.current = poly;

            const path = poly.getPath();
            const sync = () => {
                const next: GoogleMapsLatLng[] = [];
                path.forEach((latLng: google.maps.LatLng) => {
                    next.push({ lat: latLng.lat(), lng: latLng.lng() });
                });
                // This flows into parent `currentCoordinates`; parent rerender will then re-sync draft.
                onPolygonEditRef.current?.(
                    // We don't have a real lot while drafting; parent handlers ignore `place` for draft.
                    { _id: '__draft__' } as ParkingLot,
                    next
                );
            };
            draftPolygonListenersRef.current = [
                path.addListener('set_at', sync),
                path.addListener('insert_at', sync),
                path.addListener('remove_at', sync),
            ];
        } else {
            draftPolygonRef.current.setPaths(paths);
            draftPolygonRef.current.setMap(map);
        }
    }, [isMapLoaded, editMode, creatingNew, currentCoordinates]);

    // Bulk select: when active, disable map dragging and attach mousedown/mousemove/mouseup
    // listeners to draw a selection rectangle. Fires onBulkRectDrawn with the final bounds.
    useEffect(() => {
        if (!isMapLoaded || !mapRef.current || !window.google?.maps) return;

        const map = mapRef.current;

        if (!bulkSelectMode) {
            map.setOptions({ draggable: true, gestureHandling: 'auto' });
            bulkListenersRef.current.forEach((l) => l.remove());
            bulkListenersRef.current = [];
            if (bulkRectRef.current) {
                bulkRectRef.current.setMap(null);
                bulkRectRef.current = null;
            }
            return;
        }

        map.setOptions({ draggable: false, gestureHandling: 'none' });

        let startLatLng: google.maps.LatLng | null = null;

        const onDown = map.addListener('mousedown', (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return;
            startLatLng = e.latLng;
            if (bulkRectRef.current) {
                bulkRectRef.current.setMap(null);
                bulkRectRef.current = null;
            }
            bulkRectRef.current = new window.google.maps.Rectangle({
                bounds: {
                    north: e.latLng.lat(),
                    south: e.latLng.lat(),
                    east: e.latLng.lng(),
                    west: e.latLng.lng(),
                },
                map,
                strokeColor: '#2563eb',
                strokeOpacity: 0.85,
                strokeWeight: 2,
                fillColor: '#3b82f6',
                fillOpacity: 0.12,
                clickable: false,
                zIndex: 100,
            });
        });

        const onMove = map.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
            if (!startLatLng || !bulkRectRef.current || !e.latLng) return;
            bulkRectRef.current.setBounds({
                north: Math.max(startLatLng.lat(), e.latLng.lat()),
                south: Math.min(startLatLng.lat(), e.latLng.lat()),
                east: Math.max(startLatLng.lng(), e.latLng.lng()),
                west: Math.min(startLatLng.lng(), e.latLng.lng()),
            });
        });

        const onUp = map.addListener('mouseup', (e: google.maps.MapMouseEvent) => {
            if (!startLatLng || !e.latLng) return;
            const rect = bulkRectRef.current;
            if (rect) {
                rect.setMap(null);
                bulkRectRef.current = null;
            }
            const north = Math.max(startLatLng.lat(), e.latLng.lat());
            const south = Math.min(startLatLng.lat(), e.latLng.lat());
            const east = Math.max(startLatLng.lng(), e.latLng.lng());
            const west = Math.min(startLatLng.lng(), e.latLng.lng());
            startLatLng = null;
            // Ignore single clicks (no meaningful area drawn)
            if (north - south < 0.00005 && east - west < 0.00005) return;
            onBulkRectDrawnRef.current?.(north, south, east, west);
        });

        bulkListenersRef.current = [onDown, onMove, onUp];

        return () => {
            map.setOptions({ draggable: true, gestureHandling: 'auto' });
            bulkListenersRef.current.forEach((l) => l.remove());
            bulkListenersRef.current = [];
            if (bulkRectRef.current) {
                bulkRectRef.current.setMap(null);
                bulkRectRef.current = null;
            }
        };
    }, [isMapLoaded, bulkSelectMode]);

    useEffect(() => {
        const polygons = polygonsRef.current;
        const polygonListeners = polygonListenersRef.current;
        const polylines = polylinesRef.current;
        const polylineListeners = polylineListenersRef.current;

        return () => {
            polygonListeners.forEach((listeners) => {
                listeners.forEach((listener) => listener.remove());
            });
            polygons.forEach((polygon) => {
                polygon.setMap(null);
            });
            polygons.clear();
            polygonListeners.clear();

            polylineListeners.forEach((listeners) => {
                listeners.forEach((listener) => listener.remove());
            });
            polylines.forEach((lotPolylines) => {
                lotPolylines.forEach((polyline) => polyline.setMap(null));
            });
            polylines.clear();
            polylineListeners.clear();

            draftPolygonListenersRef.current.forEach((l) => l.remove());
            draftPolygonListenersRef.current = [];
            if (draftPolygonRef.current) {
                draftPolygonRef.current.setMap(null);
                draftPolygonRef.current = null;
            }

            bulkListenersRef.current.forEach((l) => l.remove());
            bulkListenersRef.current = [];
            if (bulkRectRef.current) {
                bulkRectRef.current.setMap(null);
                bulkRectRef.current = null;
            }
        };
    }, []);

    const renderOverlayMarkers = useMemo(() => {
        return overlayMarkers.map(marker => {
            const label = marker.label
                ? {
                    text: marker.label,
                    color: marker.textColor ?? (marker.kind === 'search' ? '#ffffff' : '#1d4ed8'),
                    fontSize: marker.kind === 'search' ? '12px' : '11px',
                    fontWeight: '700'
                }
                : undefined;

            const icon = googleMapsReady && marker.kind !== 'address'
                ? {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: marker.scale ?? (marker.kind === 'search' ? 18 : 16),
                    fillColor: marker.color ?? (marker.kind === 'search' ? '#2563eb' : '#ffffff'),
                    fillOpacity: marker.kind === 'search' ? 0.92 : 0.96,
                    strokeColor: marker.kind === 'search' ? '#ffffff' : '#1d4ed8',
                    strokeOpacity: 1,
                    strokeWeight: marker.kind === 'search' ? 2.5 : 2
                }
                : undefined;

            return (
                <Marker
                    key={marker.id}
                    position={marker.position}
                    title={marker.title}
                    label={label}
                    icon={icon}
                    zIndex={marker.zIndex}
                    opacity={marker.opacity}
                    onClick={marker.onClick}
                />
            );
        });
    }, [googleMapsReady, overlayMarkers]);


    if (!apiKey) {
        return (
            <div className="flex items-center justify-center h-full bg-gray-100">
                <div className="text-center p-4">
                    <p className="text-red-600 font-medium">Google Maps API Key mangler</p>
                    <p className="text-sm text-gray-600 mt-2">
                        Sæt REACT_APP_GOOGLE_MAPS_API_KEY i environment variabler
                    </p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="flex items-center justify-center h-full bg-gray-100">
                <div className="text-center p-4">
                    <p className="text-red-600 font-medium">Google Maps kunne ikke indlæses</p>
                    <p className="mt-2 text-sm text-gray-600">
                        {loadError.message || 'Der opstod en fejl under indlæsning af kortet.'}
                    </p>
                </div>
            </div>
        );
    }

    if (!isGoogleMapsScriptLoaded) {
        return <div className="flex items-center justify-center h-full">Loading map...</div>;
    }

    return (
        <div className="relative w-full h-full">
            {bulkSelectMode && (
                <div
                    style={{ position: 'absolute', inset: 0, zIndex: 5, cursor: 'crosshair', pointerEvents: 'none' }}
                />
            )}
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={stableCenter}
                zoom={defaultZoom}
                onClick={onMapClick}
                onIdle={emitViewport}
                onLoad={(map) => {
                    mapRef.current = map;
                    setIsMapLoaded(true);
                }}
                onUnmount={() => {
                    mapRef.current = null;
                    setIsMapLoaded(false);
                }}
                options={{
                    disableDefaultUI: false,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: true,
                    fullscreenControl: false
                }}
            >
                {/* Polygons are now created using native Google Maps API in useEffect */}
                {renderEditMarkers}
                {renderPointMarkers}
                {renderOverlayMarkers}
            </GoogleMap>

            {/* Loading Overlay */}
            {isRenderingPolygons && (
                <div className="absolute inset-0 bg-white bg-opacity-75 z-50 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-700 font-medium">Rendering map features...</p>
                        {(visiblePolygons.length + visibleLineFeatures.length) > 0 && (
                            <p className="text-sm text-gray-600 mt-2">
                                {renderedCount.toLocaleString()} / {(visiblePolygons.length + visibleLineFeatures.length).toLocaleString()}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParkingLotMap;
